// WEB PUSH (owner, 24 Oct 2026; plan agent-status/PUSH-NOTIFICATIONS-PLAN.md
// §10). Owner: "send to all the devices they logged in", "minimum
// interaction". So:
// - Signed in on a device that already allows notifications: alerts are on,
//   no screen at all (syncPushOnOpen subscribes by itself).
// - First time on a device: one question after sign-in ("Turn on" = the one
//   tap Apple and Google require). Asked once per device.
// - Sign out stops that device; "Turn off" is remembered for that person on
//   that device, so opening the app never undoes it.
// - Nothing registers a service worker unless this browser can really get
//   push (pushSupport() === 'ok'): the reps' selling iPads in Safari never do.
// - Nothing here throws into the app.

import { api } from 'api';
import type { SavePushDeviceRequest } from 'store/api/notifications';

export type PushSupport = 'ok' | 'unsupported' | 'ios-needs-home-screen';
export type EnableResult = 'on' | 'failed' | 'blocked' | 'dismissed';

type StoredPush = { id: number; off?: boolean; userId: number };

const STORAGE_KEY = 'clix.push';
const ASKED_KEY = 'clix.push.asked';
const TURNED_OFF_KEY = 'clix.push.turnedOff';
// PAYOUT SENT (owner, 26 Oct 2026): the people who tapped Turn on here
// themselves. Kept across sign-outs, cleared only by their Turn off.
const TURNED_ON_KEY = 'clix.push.turnedOn';
const SYNC_TIMEOUT_MS = 3000;
const AUTO_TIMEOUT_MS = 10000;
const READY_TIMEOUT_MS = 5000;
const DISABLE_TIMEOUT_MS = 2000;

const readJson = <T>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // private mode or blocked storage: the device still works this session
  }
};

const removeKey = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // nothing to clear
  }
};

export const readStoredPush = (): StoredPush | null => {
  const value = readJson<StoredPush | null>(STORAGE_KEY, null);
  return value && typeof value.id === 'number' ? value : null;
};

const writeStoredPush = (value: StoredPush) => writeJson(STORAGE_KEY, value);
const clearStoredPush = () => removeKey(STORAGE_KEY);

// "Turn off" for this person on this device (kept across sign-outs).
export const isTurnedOff = (userId: number) =>
  readJson<number[]>(TURNED_OFF_KEY, []).includes(userId);

// Turned on here by this person's own tap (the mirror of isTurnedOff).
export const isTurnedOn = (userId: number) =>
  readJson<number[]>(TURNED_ON_KEY, []).includes(userId);

const setTurnedOn = (userId: number, on: boolean) => {
  const others = readJson<number[]>(TURNED_ON_KEY, []).filter(
    (id) => id !== userId
  );
  writeJson(TURNED_ON_KEY, on ? [...others, userId] : others);
};

export const setTurnedOff = (userId: number, off: boolean) => {
  const others = readJson<number[]>(TURNED_OFF_KEY, []).filter(
    (id) => id !== userId
  );
  writeJson(TURNED_OFF_KEY, off ? [...others, userId] : others);
  // The two never hold the same person.
  if (off) {
    setTurnedOn(userId, false);
  }
};

export const markAsked = () => writeJson(ASKED_KEY, true);

const withTimeout = <T>(promise: Promise<T>, ms: number) =>
  Promise.race<T | undefined>([
    promise,
    new Promise<undefined>((resolve) =>
      setTimeout(() => resolve(undefined), ms)
    ),
  ]);

// iPadOS Safari calls itself "Macintosh"; touch points tell it apart.
export const isIosDevice = () => {
  const ua = navigator.userAgent || '';
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
};

// iPhone only (not iPad): reps sell on iPads in Safari, keep those quiet.
export const isIphone = () => /iPhone|iPod/.test(navigator.userAgent || '');

const isStandalone = () =>
  (navigator as Navigator & { standalone?: boolean }).standalone === true ||
  window.matchMedia?.('(display-mode: standalone)').matches === true;

export const pushSupport = (): PushSupport => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'unsupported';
  }
  // iPhone/iPad: only the Home Screen app can get push (WebKit).
  if (isIosDevice() && !isStandalone()) {
    return 'ios-needs-home-screen';
  }
  const capable =
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  return capable ? 'ok' : 'unsupported';
};

// The one automatic question after sign-in: only where it can work, only
// once per device, never after this person turned it off here.
export const shouldAskToTurnOn = (userId?: number) =>
  !!userId &&
  pushSupport() === 'ok' &&
  Notification.permission === 'default' &&
  !readJson<boolean>(ASKED_KEY, false) &&
  !isTurnedOff(userId);

export const deviceLabel = () => {
  const ua = navigator.userAgent || '';
  const device = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : 'Computer';
  const app = isStandalone()
    ? 'Home Screen'
    : /Edg\//.test(ua)
      ? 'Edge'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';
  return `${device} · ${app}`;
};

export const urlBase64ToUint8Array = (value: string) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => {
  if (!a) {
    return false;
  }
  const bytes = new Uint8Array(a);
  return bytes.length === b.length && bytes.every((value, i) => value === b[i]);
};

const registerWorker = async () => {
  await navigator.serviceWorker.register('/sw.js', {
    scope: '/',
    updateViaCache: 'none',
  });
  return (
    (await withTimeout(navigator.serviceWorker.ready, READY_TIMEOUT_MS)) ?? null
  );
};

// Subscribe this browser and save it for this person (permission granted).
const subscribeAndSave = async ({
  userId,
  publicKey,
  registration,
}: {
  userId: number;
  publicKey: string;
  registration: ServiceWorkerRegistration;
}) => {
  const options = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  };
  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe(options);
  } catch (error) {
    // Only an old subscription made with another key is replaced; any other
    // failure keeps the working one (the next open tries again).
    const existing = await registration.pushManager.getSubscription();
    if (
      !existing ||
      sameKey(
        existing.options?.applicationServerKey,
        options.applicationServerKey
      )
    ) {
      throw error;
    }
    await existing.unsubscribe();
    subscription = await registration.pushManager.subscribe(options);
  }
  const json = subscription.toJSON();
  const body: SavePushDeviceRequest = {
    deviceLabel: deviceLabel(),
    endpoint: String(json.endpoint),
    keys: {
      auth: String(json.keys?.auth),
      p256dh: String(json.keys?.p256dh),
    },
  };
  const { data } = await api.notifications.savePushDevice(body);
  writeStoredPush({ userId, id: data.id });
  return data.id;
};

let syncRunning: Promise<void> = Promise.resolve();

// Switch off a subscription that is no longer this person's; returns whether
// this browser is still on for them.
const cleanUp = async (userId: number) => {
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = registration
    ? await registration.pushManager.getSubscription()
    : null;
  const stored = readStoredPush();

  // Checked first: a Turn off / Sign out whose unsubscribe did not finish.
  if (stored?.off) {
    await subscription?.unsubscribe();
    clearStoredPush();
    return false;
  }
  if (!subscription) {
    clearStoredPush();
    return false;
  }
  // Shared device, or a subscription this browser no longer knows about.
  if (!stored || stored.userId !== userId) {
    await subscription.unsubscribe();
    clearStoredPush();
    return false;
  }
  // Deleted on the server (the push service dropped it): start fresh.
  const { data } = await api.notifications.getPushDevices(
    AbortSignal.timeout(SYNC_TIMEOUT_MS)
  );
  if (Array.isArray(data) && !data.some((device) => device.id === stored.id)) {
    await subscription.unsubscribe();
    clearStoredPush();
    return false;
  }
  return true;
};

// Signed in on a device that already allows notifications: turn them on for
// this person with no screen.
const turnOnQuietly = async (userId: number) => {
  if (Notification.permission !== 'granted' || isTurnedOff(userId)) {
    return;
  }
  const { data: config } = await api.notifications.getPushConfig();
  if (!config?.enabled || !config.publicKey) {
    return;
  }
  const registration = await registerWorker();
  if (registration) {
    await subscribeAndSave({
      userId,
      registration,
      publicKey: config.publicKey,
    });
  }
};

// On every app open (signed in). Bounded, never throws; an API 404 or error
// changes nothing. Turned back on quietly for people with the bell, and
// (PAYOUT SENT) for anyone who turned it on here themselves; never for
// someone who did not (a rep signing in on an office PC is never subscribed).
export const syncPushOnOpen = (userId?: number, hasNotifications = false) => {
  syncRunning = (async () => {
    try {
      if (pushSupport() !== 'ok' || !userId) {
        return;
      }
      const on = await withTimeout(cleanUp(userId), SYNC_TIMEOUT_MS);
      if (on === false && (hasNotifications || isTurnedOn(userId))) {
        await withTimeout(turnOnQuietly(userId), AUTO_TIMEOUT_MS);
      }
    } catch {
      // keep everything as it is
    }
  })();
  return syncRunning;
};

export const pushSyncSettled = () => syncRunning;

// Before a "Turn on" tap: register the worker and wait until it is active, so
// the tap can subscribe straight away.
export const preparePush = async () => {
  if (pushSupport() !== 'ok') {
    return null;
  }
  await syncRunning;
  try {
    return await registerWorker();
  } catch {
    return null;
  }
};

export const enablePush = async ({
  userId,
  publicKey,
  registration,
}: {
  userId: number;
  publicKey: string;
  registration: ServiceWorkerRegistration;
}): Promise<{ id?: number; result: EnableResult }> => {
  try {
    // First await of the tap: Safari only asks inside the user's gesture.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { result: permission === 'denied' ? 'blocked' : 'dismissed' };
    }
    setTurnedOff(userId, false);
    const id = await subscribeAndSave({ userId, publicKey, registration });
    setTurnedOn(userId, true);
    return { id, result: 'on' };
  } catch {
    return { result: 'failed' };
  }
};

// Sign out or Turn off. Always unsubscribes this browser, stored state or
// not. On Sign out the row is also deleted on the server while the token is
// still valid (bounded; any error is ignored); otherwise the unsubscribed
// endpoint answers 404/410 and the server deletes the row on the next send.
// "off" is kept until unsubscribe finishes, so the next open completes it
// whoever signs in.
export const disablePushHere = async ({
  removeOnServer = false,
}: { removeOnServer?: boolean } = {}) => {
  if (pushSupport() !== 'ok') {
    clearStoredPush();
    return;
  }
  const stored = readStoredPush();
  if (stored) {
    writeStoredPush({ ...stored, off: true });
  }
  const work = (async () => {
    try {
      if (removeOnServer && stored) {
        await api.notifications
          .deletePushDevice(stored.id, AbortSignal.timeout(DISABLE_TIMEOUT_MS))
          .catch(() => undefined);
      }
      const registration = await navigator.serviceWorker.getRegistration('/');
      const subscription = await registration?.pushManager.getSubscription();
      await subscription?.unsubscribe();
      clearStoredPush();
    } catch {
      // "off" stays; the next open finishes it
    }
  })();
  await withTimeout(work, DISABLE_TIMEOUT_MS);
};

// PAYOUT SENT (owner, 26 Oct 2026): a tapped personal alert asks the open app
// (through the service worker) to go to its page. Never during a sale: every
// selling screen lives under /products/; the alert stays in the bell.
export const SELLING_PATH_PREFIX = '/products/';

export const listenForOpenLink = (navigate: (to: string) => void) => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return () => undefined;
  }
  const onMessage = (event: MessageEvent) => {
    const data = event?.data;
    if (
      !data ||
      data.type !== 'open-link' ||
      typeof data.link !== 'string' ||
      !data.link.startsWith('/') ||
      data.link.startsWith('//')
    ) {
      return;
    }
    if (window.location.pathname.startsWith(SELLING_PATH_PREFIX)) {
      return;
    }
    navigate(data.link);
  };
  navigator.serviceWorker.addEventListener('message', onMessage);
  return () =>
    navigator.serviceWorker.removeEventListener('message', onMessage);
};
